// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/SparringReflejos.jsx
//  FORMATO 1 — SPARRING DE REFLEJOS
//
//  Spacy suelta una frase. Aparecen dos respuestas. Cuatro segundos para elegir.
//
//  ── LA PEDAGOGÍA ────────────────────────────────────────────────────────────
//  El reloj corto es el método, no el adorno: cuatro segundos es menos de lo
//  que tarda un cerebro en traducir inglés → español → decidir → inglés. Al
//  quitar el tiempo físico de traducir, el alumno se ve obligado a reconocer el
//  bloque correcto de oído. Eso es lo que hace un hablante fluido.
//
//  La opción equivocada nunca es un disparate: es lo que un hispanohablante
//  diría de verdad ("I am agree", "I have 30 years"). El alumno se reconoce en
//  ella, y ese reconocimiento enseña más que la explicación.
//
//  ── POR QUÉ YA NO SE RESPONDE HABLANDO ──────────────────────────────────────
//  La primera versión pedía responder por micrófono y comparaba lo dicho contra
//  una lista de bloques aceptados. A "your mic is muted" hay cientos de
//  respuestas buenas, así que el sistema marcaba error en respuestas correctas
//  — el peor fallo posible en un entrenador. Con dos opciones el juicio es
//  exacto, y de paso el formato funciona en Safari, sin permisos de micrófono
//  y sin ruido de fondo.
//
//  El habla no se pierde: se entrena en Shadowing y en Crisis Room, que son los
//  dos sitios donde sí se puede medir con honestidad.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { speak, cancelSpeech, isSpeechAvailable } from '../utils/browserSpeech';
import { SPARRING, byContext, shuffle } from './dojoBank';
import { fxHit, fxCombo, fxMiss, fxTimeout, fxTick, fxStart } from '../audio/fx';
import { useFeedback } from './useFeedback';
import FeedbackLayer from './FeedbackLayer';
import DojoResult from './DojoResult';

const ROUNDS = 8;
const CHOOSE_MS = 4000;   // ventana para elegir

export default function SparringReflejos({ ctx, onExit }) {
  const [deck] = useState(() => shuffle(byContext(SPARRING, ctx)).slice(0, ROUNDS));
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState('intro');  // intro|pending|say|choose|verdict|done
  const [picked, setPicked] = useState(null);   // índice elegido, null si expiró
  const [hits, setHits] = useState(0);
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(0);
  const [times, setTimes] = useState([]);
  const [bar, setBar] = useState(1);

  const { flash, floats, fire } = useFeedback();

  const rafRef = useRef(0);
  const timerRef = useRef(null);
  const openedAt = useRef(0);
  const lastTick = useRef(0);

  const item = deck[i];
  const canSpeak = isSpeechAvailable();

  /* ── Orden de las opciones ──────────────────────────────────────────────
     Se baraja una vez por reactivo, así que la correcta cae en A o en B sin
     patrón. Va en `useMemo` atado al id: si se rebarajara en cada render, las
     opciones bailarían mientras el alumno decide. */
  const options = useMemo(
    () => shuffle([{ text: item.good, ok: true }, { text: item.bad, ok: false }]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.id],
  );

  const stopAll = useCallback(() => {
    cancelSpeech();
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);
  }, []);

  useEffect(() => stopAll, [stopAll]);

  /* ── Cuenta regresiva ──────────────────────────────────────────────────── */
  const runBar = useCallback((ms, onEnd) => {
    const t0 = performance.now();
    lastTick.current = 0;
    const step = (now) => {
      const left = Math.max(0, 1 - (now - t0) / ms);
      setBar(left);

      // Un tic por segundo restante: el sonido marca la urgencia mejor que el color.
      const sec = Math.ceil((left * ms) / 1000);
      if (sec !== lastTick.current && sec > 0) {
        lastTick.current = sec;
        fxTick(left < 0.34);
      }

      if (left <= 0) { onEnd(); return; }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  const closeRound = useCallback((idx) => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(timerRef.current);

    const ok = idx !== null && options[idx].ok;
    setPicked(idx);
    setPhase('verdict');

    if (ok) {
      setHits((h) => h + 1);
      setStreak((s) => {
        const n = s + 1;
        setBest((b) => Math.max(b, n));
        if (n >= 3) fxCombo(n); else fxHit();
        fire(true, n >= 3 ? `+1 ×${n}` : '+1');
        return n;
      });
    } else {
      setStreak(0);
      if (idx === null) { fxTimeout(); fire(false, '⏱'); }
      else { fxMiss(); fire(false, '✕'); }
    }
  }, [options, fire]);

  /* ── Ciclo de una ronda ────────────────────────────────────────────────── */
  const startRound = useCallback(() => {
    setPicked(null);
    setBar(1);
    setPhase('say');

    const open = () => {
      openedAt.current = performance.now();
      setPhase('choose');
      runBar(CHOOSE_MS, () => closeRound(null));
    };

    if (canSpeak) {
      speak(item.say, { rate: 1.0 }, { onEnd: open, onError: open });
    } else {
      // Sin voz: la frase ya está en pantalla, la ventana se abre igual.
      timerRef.current = setTimeout(open, 900);
    }
  }, [item, canSpeak, runBar, closeRound]);

  // El arranque se dispara por FASE y no con un setTimeout encadenado: si se
  // hiciera a mano, el temporizador ejecutaría la versión de `startRound` del
  // render anterior y Spacy diría la frase del reactivo que ya pasó.
  useEffect(() => {
    if (phase === 'pending') startRound();
  }, [phase, startRound]);

  const choose = useCallback((idx) => {
    if (phase !== 'choose') return;
    setTimes((t) => [...t, Math.round(performance.now() - openedAt.current)]);
    closeRound(idx);
  }, [phase, closeRound]);

  const next = useCallback(() => {
    if (i + 1 >= deck.length) { setPhase('done'); return; }
    setI((n) => n + 1);
    setPhase('pending');
  }, [i, deck.length]);

  const begin = () => { fxStart(); setPhase('pending'); };

  /* ── Teclado: A/B, 1/2 y flechas; Enter para avanzar ───────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (phase === 'choose') {
        if (k === 'a' || k === '1' || k === 'arrowleft') choose(0);
        if (k === 'b' || k === '2' || k === 'arrowright') choose(1);
      } else if (phase === 'verdict' && (k === 'enter' || k === ' ')) {
        e.preventDefault();
        next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, choose, next]);

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (phase === 'done') {
    const avg = times.length
      ? Math.round(times.reduce((a, b) => a + b, 0) / times.length)
      : 0;
    return (
      <DojoResult
        format="sparring"
        score={hits}
        total={deck.length}
        headline={`${hits}/${deck.length}`}
        label="Reflejos conectados"
        recap={[
          { v: best, k: 'mejor racha' },
          { v: avg ? `${(avg / 1000).toFixed(1)}s` : '—', k: 'reacción media' },
          { v: `${Math.round((hits / deck.length) * 100)}%`, k: 'aciertos' },
        ]}
        note={avg && avg < 1500
          ? 'Estás reconociendo el bloque, no traduciéndolo. Eso es exactamente el objetivo.'
          : 'Cuanto más baja tu reacción media, menos estás traduciendo en la cabeza.'}
        onAgain={() => window.location.reload()}
        onExit={onExit}
      />
    );
  }

  if (phase === 'intro') {
    return (
      <div className="dj-stage dj-screen">
        <p className="dj-hero-tag">⚡ Formato 01 · Reacción</p>
        <h2 className="dj-h1"><span className="dj-glow">SPARRING</span></h2>
        <p className="dj-lead">
          Spacy te lanza una frase y aparecen dos respuestas. Tienes
          {' '}<b>4 segundos</b> para elegir la que diría un nativo. No la
          analices — reconócela.
        </p>
        <button type="button" className="dj-btn" style={{ width: '100%' }} onClick={begin}>
          Empezar ({deck.length} rondas) →
        </button>
      </div>
    );
  }

  const chosen = picked !== null ? options[picked] : null;
  const won = Boolean(chosen?.ok);
  const stageClass = phase === 'verdict'
    ? `dj-stage ${won ? 'dj-good' : 'dj-bad'}`
    : 'dj-stage';

  return (
    <>
      <FeedbackLayer flash={flash} floats={floats} />

      <div className="dj-hud">
        <button type="button" className="dj-quit" onClick={() => { stopAll(); onExit(); }}>← Salir</button>
        <span className="dj-badge">Sparring</span>
        <span className="dj-spacer" />
        {streak >= 3 && <span className="dj-streak dj-pulse" key={streak}>🔥 ×{streak}</span>}
        <span className="dj-score-badge">{hits}</span>
      </div>

      <div className="dj-timer">
        <div
          className={`dj-timer-fill ${bar < 0.34 ? 'dj-crit' : bar < 0.62 ? 'dj-warn' : ''}`}
          style={{ transform: `scaleX(${phase === 'choose' ? bar : phase === 'verdict' ? 0 : 1})` }}
        />
      </div>
      <div className="dj-meta" style={{ marginTop: '-0.85rem', marginBottom: '1rem' }}>
        <span className="dj-progress">Ronda {i + 1} de {deck.length}</span>
        <span className="dj-progress">Mejor racha {best}</span>
      </div>

      <div className={stageClass}>
        <span className="dj-section-lbl">{item.hint}</span>
        <p className="dj-said">“{item.say}”</p>

        {phase !== 'verdict' && (
          <div className={`dj-wave ${phase === 'say' || phase === 'pending' ? '' : 'dj-idle'}`} aria-hidden="true">
            <i /><i /><i /><i /><i /><i />
          </div>
        )}

        {phase === 'verdict' && (
          <>
            <p className={`dj-verdict ${won ? 'dj-good-t' : 'dj-bad-t'}`}>
              {won ? '¡Conectado!' : picked === null ? 'Se te fue' : 'Fallaste'}
            </p>
            {!won && <p className="dj-why">{item.why}</p>}
          </>
        )}
      </div>

      <div className="dj-opts">
        {options.map((o, idx) => {
          const state = phase === 'verdict'
            ? (o.ok ? ' dj-opt-ok' : (picked === idx ? ' dj-opt-no' : ' dj-opt-off'))
            : '';
          return (
            <button
              key={o.text}
              type="button"
              className={`dj-opt${state}`}
              onClick={() => choose(idx)}
              disabled={phase !== 'choose'}
            >
              <span className="dj-opt-letter">{idx === 0 ? 'A' : 'B'}</span>
              {o.text}
            </button>
          );
        })}
      </div>

      {phase === 'verdict' && (
        <button type="button" className="dj-btn dj-mic" onClick={next}>
          {i + 1 >= deck.length ? 'Ver resultado →' : 'Siguiente →'}
        </button>
      )}
    </>
  );
}
